/**
 * MAHI — money formatting.
 *
 * Every price on the site used to be written as `₹{n.toLocaleString()}`, with
 * the rupee sign hardcoded at each of the six call sites. The catalogue is
 * crawled from hundreds of storefronts and some of them price in USD, so that
 * was quietly relabelling a $180 dress as ₹180 — off by ~85x, and in the
 * direction that makes something look like a bargain. Wrong price is the one
 * error here that costs a real person real money, so the currency stored on
 * the row is now the only thing that decides the symbol.
 *
 * `Intl.NumberFormat` gives us the symbol and the grouping together, which
 * matters because they are not independent: ₹ goes with lakh grouping
 * (₹1,23,456) and $ with thousands (`$123,456`). Doing the symbol by hand and
 * the grouping by locale would produce ₹123,456 — right symbol, wrong shape.
 */

// Currency → locale, chosen for grouping style rather than language.
// Anything not listed falls back to en-US, whose plain thousands grouping is
// the safe default for a currency we have not thought about yet.
const LOCALE_BY_CURRENCY = {
  INR: 'en-IN',
  USD: 'en-US',
  GBP: 'en-GB',
  EUR: 'en-IE',
  AED: 'en-AE',
  SGD: 'en-SG',
  AUD: 'en-AU',
  CAD: 'en-CA',
};

// The catalogue is India-first: a row that never got a currency written to it
// is overwhelmingly likely to be INR, and that is also what the crawler's own
// normalisation falls back to (crawler_service._normalize_product).
const DEFAULT_CURRENCY = 'INR';

/** Normalise whatever the row carries into a plausible ISO-4217 code. */
function normaliseCode(currency) {
  const code = String(currency || '').trim().toUpperCase();
  // ISO-4217 is always three letters; anything else is junk from a crawl and
  // should fall back rather than be handed to Intl (which throws on it).
  return /^[A-Z]{3}$/.test(code) ? code : DEFAULT_CURRENCY;
}

/**
 * Format an amount in the product's own currency.
 *
 * @param {number|string|null} amount  raw price off the product row
 * @param {string} currency            ISO-4217 code, e.g. 'INR' | 'USD'
 * @returns {string}                   e.g. '₹2,499' | '$180'
 */
export function formatMoney(amount, currency) {
  const value = amount == null || amount === '' ? null : Number(amount);
  if (value == null || Number.isNaN(value)) return '';

  const code = normaliseCode(currency);
  const locale = LOCALE_BY_CURRENCY[code] || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      // Catalogue prices are whole units; showing ₹2,499.00 adds noise and
      // costs horizontal space on a card that is already tight.
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Intl throws RangeError on a code it doesn't recognise. A crawled row
    // should never take a price off the screen, so degrade to code + number.
    return `${code} ${value.toLocaleString(locale)}`;
  }
}

/**
 * The effective price a shopper pays — offer price when it is a genuine
 * discount, list price otherwise.
 *
 * Centralised because all six price components had their own copy of this
 * `offer != null && offer < price` test, and they did not all agree on what
 * to do when `offer` was present but higher than `price` (bad crawl data).
 *
 * @returns {{ current: number|null, was: number|null, isDiscounted: boolean }}
 */
export function effectivePrice(product) {
  const price = product?.price != null && product.price !== '' ? Number(product.price) : null;
  const offer = product?.offer_price != null && product.offer_price !== '' ? Number(product.offer_price) : null;
  const validPrice = price != null && !Number.isNaN(price) ? price : null;
  const validOffer = offer != null && !Number.isNaN(offer) ? offer : null;
  const isDiscounted = validPrice != null && validOffer != null && validOffer < validPrice;
  return {
    current: isDiscounted ? validOffer : validPrice,
    was: isDiscounted ? validPrice : null,
    isDiscounted,
  };
}
